import type { AppliedLimit, PeriodBounds } from '@/lib/api/generated/model';

/** Fewer elapsed days than this and an average says nothing yet (spec 099 FR-003). */
export const MIN_ELAPSED_DAYS = 3;

const DAY_MS = 86_400_000;

export interface SpendForecast {
  /** Projected total by the period's end, as a two-decimal string. */
  projectedTotal: string;
  /** The period's last day. */
  endsAt: Date;
  /** The day the limit would be crossed at this pace, or null when it would not. */
  limitReachedOn: Date | null;
  /** Spend already meets the limit. */
  limitReached: boolean;
}

export interface SpendForecastInput {
  spendAmount: string;
  limit: Pick<AppliedLimit, 'amount'> | null;
  period: Pick<PeriodBounds, 'starts_at' | 'ends_at' | 'is_current'>;
  now?: Date;
}

// UTC, not local: `period.starts_at`/`ends_at` are UTC timestamps (typically
// midnight-anchored, `ends_at` at 23:59:59), and bucketing them by local
// calendar day shifts the day boundary in any timezone east of UTC — e.g.
// `2026-09-30T23:59:59Z` rolls over to October 1st local in UTC+1 or later,
// silently inflating every day count derived from it.
const startOfDay = (d: Date) =>
  new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
const daysBetweenInclusive = (from: Date, to: Date) =>
  Math.floor((startOfDay(to).getTime() - startOfDay(from).getTime()) / DAY_MS) +
  1;

/**
 * Where the period is heading at its average daily pace (spec 099 FR-001 –
 * FR-003). A straight average, worded as "about" wherever it is shown: the
 * exact figures on the page keep their exact rendering; this is the one
 * estimate, and it says so.
 *
 * Nothing is said for a past period, a lifetime window (no end), fewer than
 * three elapsed days, or no spend — an average of one day is a guess with a
 * decimal point.
 */
export function forecastSpend({
  spendAmount,
  limit,
  period,
  now = new Date(),
}: SpendForecastInput): SpendForecast | null {
  if (!period.is_current || period.ends_at === null) return null;
  const spend = Number(spendAmount);
  if (!Number.isFinite(spend) || spend <= 0) return null;
  const start = new Date(period.starts_at);
  const end = new Date(period.ends_at);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;

  const elapsed = daysBetweenInclusive(start, now);
  if (elapsed < MIN_ELAPSED_DAYS) return null;
  const total = daysBetweenInclusive(start, end);
  const remaining = Math.max(total - elapsed, 0);
  const perDay = spend / elapsed;
  const projected = spend + perDay * remaining;

  let limitReachedOn: Date | null = null;
  let limitReached = false;
  if (limit) {
    const cap = Number(limit.amount);
    if (Number.isFinite(cap) && cap > 0) {
      if (spend >= cap) {
        limitReached = true;
      } else if (projected >= cap) {
        const daysToCap = Math.ceil((cap - spend) / perDay);
        const on = new Date(startOfDay(now).getTime() + daysToCap * DAY_MS);
        limitReachedOn = on > end ? end : on;
      }
    }
  }

  return {
    projectedTotal: projected.toFixed(2),
    endsAt: end,
    limitReachedOn,
    limitReached,
  };
}
